import { yupResolver } from "@hookform/resolvers";
import { Paper, TextField } from "@material-ui/core";
import Axios from "axios";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import styled from "styled-components";
import CustomButton from "../../../../../../components/Button/CustomButton";
import InputValidationMessage from "../../../../../../components/forms/InputValidationMessage/InputValidationMessage";
import { warehousesSchema } from "../../../../../../services/formValidation";
import { errorToast, successToast } from "../../../../../../services/toasts";
import { useGetCities } from "../../../../../../utils/react-query/queryCheckout/queries";

type DispatchAddress = {
  id: number;
  name: string;
  street_address: string;
  city_name: string;
};

type EditDraft = {
  name: string;
  street_address: string;
  city_name: string;
};

const Warehouses = () => {
  const [warehouses, setWarehouses] = useState<DispatchAddress[]>();
  const [addressCount, setAddressCount] = useState(0);
  const [isAddingAddress, setIsAddingAddress] = useState(false);

  // Edit state: which warehouse is open for editing and its current draft values
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft>({
    name: "",
    street_address: "",
    city_name: "",
  });

  const { register, errors, handleSubmit, control } = useForm({
    resolver: yupResolver(warehousesSchema),
    defaultValues: {
      name: "",
      street_address: "",
      city_name: "",
    },
  });

  const { data: cityOptions, isLoading } = useGetCities();

  const getWarehouses = async () => {
    const { data } = await Axios.get(`/v2/suppliers/storage-address`);
    setWarehouses(data);
    setAddressCount(data?.length);
  };

  useEffect(() => {
    getWarehouses();
  }, []);

  const handleAddNewAddress = async (data: any) => {
    try {
      if (addressCount < 3) {
        const response = await Axios.post(`/v2/suppliers/storage-address`, data);
        setWarehouses([...warehouses!, response.data]);
        successToast("Dirección de bodega agregada exitosamente");
        setIsAddingAddress(false);
        setAddressCount((prev) => prev + 1);
      } else {
        errorToast("No puedes agregar más de 3 direcciones de bodega");
      }
    } catch (error) {
      errorToast("Error al agregar la dirección de bodega");
    }
  };

  const handleStartEdit = (warehouse: DispatchAddress) => {
    // Close create form if open
    setIsAddingAddress(false);
    setEditingId(warehouse.id);
    setEditDraft({
      name: warehouse.name || "",
      street_address: warehouse.street_address,
      city_name: warehouse.city_name,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = async (warehouseId: number) => {
    if (!editDraft.name.trim()) {
      errorToast("El nombre de la bodega es obligatorio");
      return;
    }
    try {
      await Axios.patch(`/v2/suppliers/storage-address/${warehouseId}`, {
        name: editDraft.name.trim(),
        street_address: editDraft.street_address,
        city_name: editDraft.city_name,
      });
      setWarehouses((prev) =>
        prev?.map((w) =>
          w.id === warehouseId
            ? { ...w, ...editDraft, name: editDraft.name.trim() }
            : w
        )
      );
      successToast("Dirección actualizada exitosamente");
      setEditingId(null);
    } catch (error) {
      errorToast("Error al actualizar la dirección");
    }
  };

  return (
    <div>
      <Paper style={{ padding: "2rem" }}>
        <h1
          style={{
            fontWeight: "normal",
            fontSize: "1.5rem",
            color: "#232f3e",
          }}
        >
          Direcciones de despacho
        </h1>
        <br />

        {warehouses && warehouses.length > 0 ? (
          warehouses.map((warehouse, index) => (
            <WarehouseCard key={warehouse.id}>
              <WarehouseHeader>
                <h3 style={{ margin: 0 }}>Dirección de bodega {index + 1}</h3>
                {editingId !== warehouse.id && (
                  <EditLink onClick={() => handleStartEdit(warehouse)}>
                    Editar
                  </EditLink>
                )}
              </WarehouseHeader>

              {editingId === warehouse.id ? (
                // Edit form — all three fields
                <div style={{ marginTop: "1rem" }}>
                  <Row>
                    <FieldWrapper>
                      <StyledLabel>Nombre de la bodega: *</StyledLabel>
                      <TextField
                        value={editDraft.name}
                        onChange={(e) =>
                          setEditDraft((d) => ({ ...d, name: e.target.value }))
                        }
                        placeholder="Ej: Bodega Central"
                        variant="outlined"
                        size="small"
                        fullWidth
                        autoFocus
                      />
                    </FieldWrapper>
                  </Row>
                  <Row>
                    <FieldWrapper>
                      <StyledLabel>Calle:</StyledLabel>
                      <TextField
                        value={editDraft.street_address}
                        onChange={(e) =>
                          setEditDraft((d) => ({
                            ...d,
                            street_address: e.target.value,
                          }))
                        }
                        placeholder="Ej: Av. 1 # 2 - 3"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    </FieldWrapper>
                    <FieldWrapper>
                      <StyledLabel>Ciudad:</StyledLabel>
                      {cityOptions && cityOptions.length > 0 && (
                        <Select
                          isLoading={isLoading}
                          placeholder="Selecciona una ciudad"
                          options={cityOptions}
                          value={
                            cityOptions.find(
                              (opt) => opt.label === editDraft.city_name
                            ) || null
                          }
                          onChange={(selected) =>
                            setEditDraft((d) => ({
                              ...d,
                              city_name: selected?.label || "",
                            }))
                          }
                        />
                      )}
                    </FieldWrapper>
                  </Row>
                  <ActionsRow>
                    <CustomButton
                      shape="little-rounded"
                      color="secondary"
                      onClick={() => handleSaveEdit(warehouse.id)}
                    >
                      Guardar cambios
                    </CustomButton>
                    <CustomButton
                      shape="little-rounded"
                      color="secondary"
                      onClick={handleCancelEdit}
                    >
                      Cancelar
                    </CustomButton>
                  </ActionsRow>
                </div>
              ) : (
                // Display mode
                <div style={{ marginTop: "0.5rem" }}>
                  {warehouse.name ? (
                    <p>
                      <strong>Nombre:</strong> {warehouse.name}
                    </p>
                  ) : (
                    <p style={{ color: "#888", fontStyle: "italic" }}>
                      Sin nombre asignado
                    </p>
                  )}
                  <p>
                    <strong>Calle:</strong> {warehouse.street_address}
                  </p>
                  <p>
                    <strong>Ciudad:</strong> {warehouse.city_name}
                  </p>
                </div>
              )}
            </WarehouseCard>
          ))
        ) : (
          <p>No hay direcciones de bodega disponibles.</p>
        )}

        <div style={{ marginTop: "1rem" }}>
          {isAddingAddress ? (
            <div>
              <h3>Agregar nueva dirección de bodega</h3>
              <form onSubmit={handleSubmit(handleAddNewAddress)}>
                <Row>
                  <FieldWrapper>
                    <StyledLabel>Nombre de la bodega: *</StyledLabel>
                    <TextField
                      inputRef={register}
                      name="name"
                      placeholder="Ej: Bodega Central"
                      variant="outlined"
                      size="small"
                      error={!!errors?.name}
                      helperText={errors?.name?.message}
                      fullWidth
                    />
                  </FieldWrapper>
                </Row>
                <Row>
                  <FieldWrapper>
                    <StyledLabel>Calle:</StyledLabel>
                    <StyledInput
                      type="text"
                      placeholder="Ej: Av. 1 # 2 - 3"
                      name="street_address"
                      ref={register}
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <StyledLabel>Ciudad:</StyledLabel>
                    {cityOptions && cityOptions.length > 0 && (
                      <Controller
                        name="city_name"
                        control={control}
                        render={({ ...field }) => (
                          <Select
                            isLoading={isLoading}
                            placeholder="Selecciona una ciudad"
                            {...field}
                            options={cityOptions}
                            onChange={(selectedOption) => {
                              field.onChange(selectedOption?.label);
                            }}
                            value={cityOptions.find(
                              (option) => option.value === field.value
                            )}
                          />
                        )}
                      />
                    )}
                  </FieldWrapper>
                </Row>
                <InputValidationMessage
                  message={errors?.street_address?.message}
                />
                <InputValidationMessage message={errors?.city_name?.message} />
                <ActionsRow>
                  <CustomButton
                    type="submit"
                    shape="little-rounded"
                    color="secondary"
                  >
                    Guardar dirección
                  </CustomButton>
                  <CustomButton
                    shape="little-rounded"
                    color="secondary"
                    onClick={() => setIsAddingAddress(false)}
                  >
                    Cancelar
                  </CustomButton>
                </ActionsRow>
              </form>
            </div>
          ) : (
            <CustomButton
              type="button"
              shape="little-rounded"
              disabled={addressCount >= 3}
              color="secondary"
              onClick={() => {
                setEditingId(null);
                setIsAddingAddress(true);
              }}
            >
              Agregar dirección de bodega
            </CustomButton>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default Warehouses;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 0;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 1rem;
  }
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin: 0.25rem 0;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
`;

const WarehouseCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
`;

const WarehouseHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledLabel = styled.label`
  font-family: "Open Sans";
  margin-bottom: 5px;
  font-size: 16px;
  font-weight: 300;
`;

const StyledInput = styled.input`
  font-family: "Open sans";
  padding: 0.4rem 1rem;
`;

const EditLink = styled.span`
  color: #1976d2;
  cursor: pointer;
  font-size: 0.875rem;
  text-decoration: underline;

  &:hover {
    color: #115293;
  }
`;
